import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { SoundNotificationContext, useNotificationsV2 } from '@uni-space/uni-components';
import { ErrorRow, ParcelAcceptanceRequest, StatusAcceptanceEnum, SuccessRow } from './consolidator';
import { acceptance } from '../api';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ErrorDTO } from '@shared/types/api';
import { getErrorMessageAsync } from '@shared/api';
import { useLabelPrinter } from '@shared/labels';

export type useFetchSendDataProps = {
  addSuccessRow: (parcel: SuccessRow) => void;
  addErrorRow: (error: ErrorRow) => void;
  setIsLoading: (isLoading: boolean) => void;
};

export const useFetchSendData = (props: useFetchSendDataProps) => {
  const { addSuccessRow, addErrorRow, setIsLoading } = props;
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { createNotification } = useNotificationsV2();
  const { t } = useTranslation();
  const { printLabelFromBase64 } = useLabelPrinter({
    errorTitle: t('common.systemError.title'),
    fallbackErrorMessage: t('common.systemError.desc'),
  });

  const { mutateAsync: fetchSendData } = useMutation({
    mutationKey: ['acceptance'],
    mutationFn: (requestData: ParcelAcceptanceRequest) => acceptance(requestData),
    onSuccess: (data, requestData) => {
      if (data.status === StatusAcceptanceEnum.Accepted) {
        const newResult = {
          trackNumber: data.parcel.trackNumber,
          regionZone: data.parcel.regionZone,
          weight: data.parcel.actualWeight.value,
          scanAt: data.parcel.scanAt,
          bagCode: data.parcel.bagCode,
          id: data.parcel.trackNumber,
          sortingCode: data.parcel.sortingCode,
        };
        addSuccessRow(newResult);
        createNotification({ type: 'success', message: { title: t('parcelSuccessScan') } });
        onSoundSuccess();

        if (data.parcel?.postalLabel) {
          void printLabelFromBase64(data.parcel.postalLabel);
        }

        if (data.parcel?.azLabel) {
          void printLabelFromBase64(data.parcel.azLabel);
        }

      } else {
        createNotification({ type: 'error', message: { title: t('parcelErrorScan') } });
        addErrorRow({
          trackNumber: requestData.trackNumber,
          status: data.status,
          id: `${new Date()}`,
        });
        onSoundError();
      }
    },
    onError: async (e: AxiosError<ErrorDTO>) => {
      const message = await getErrorMessageAsync(e, t('common.systemError.desc'));
      createNotification({
        type: 'error',
        message: { title: t('common.systemError.title'), desc: message },
      });
      onSoundError();
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  return { fetchSendData };
};
